import { clsx } from 'clsx';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { createPalette, PaletteAtomButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomButton,
    AtomLink,
    DialogRoot,
    LoadingIndicator,
    SidebarEmptyState,
    Tooltip,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import {
    CreateSiteDialog,
    getSiteNotPublishableReason,
    SiteContentIcon,
    UpgradeDialogProvider,
    useSitePublish,
} from '../../Components';
import { SiteSortBy, useSortSites } from '../../Components/Sites';
import { checkIsSiteStructureEmpty } from '../../state';
import { useGetSiteListPaletteGroups } from './SettingsPalette';

/**
 * Pressable current-site summary that opens the site switcher palette.
 */
export function SiteSwitcherButton(props: {
    screen: NavigationScreenState;
    organization: api.Organization;
    canCreateSite: boolean;
    organizationContent: api.OrganizationAllContent;
    site: api.Site;
}) {
    const { canCreateSite, organization, organizationContent, screen, site } = props;
    const createSiteDialogTrigger = useDialogTriggerState();
    const getSiteListPaletteGroups = useGetSiteListPaletteGroups();
    const { sortedSites } = useSortSites({
        allSites: organizationContent.sites,
        organizationId: organization.id,
        sortBy: SiteSortBy.Viewed,
    });
    const shouldShowActionsDivider =
        Boolean(site.urls.published) ||
        (site.permissions.admin &&
            ((site.published && site.visibility === api.SiteVisibility.ShareLink) ||
                !site.published));
    const siteStructure = organizationContent.sites.find(
        (siteContent) => siteContent.site.id === site.id
    )?.structure;
    const isSiteEmpty = siteStructure ? checkIsSiteStructureEmpty(siteStructure) : true;
    const trackingContext = {
        type: 'click',
        action: 'open',
        organization: organization.id,
        site: site.id,
        screen: screen.type,
        screenSection: 'site_switcher',
    } satisfies Omit<analytics.ClickAnalyticsEvent, 'component'>;

    const siteListPalette = () =>
        createPalette({
            groups: getSiteListPaletteGroups({
                currentSiteId: site.id,
                organizationContent: {
                    ...organizationContent,
                    sites: sortedSites.map(({ item }) => item),
                },
                organizationId: organization.id,
                onCreateSite: canCreateSite ? createSiteDialogTrigger.open : undefined,
                trackingContext,
            }),
        });

    return (
        <>
            <div className="flex w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-neutral-subtle">
                <PaletteAtomButton
                    palette={siteListPalette}
                    className="group/sidebar-item data-[focus-visible]:focus-ring-inset flex w-full min-w-0 flex-col gap-1.5 bg-base px-2.5 py-2 text-left text-base hover:bg-neutral-ui-hover data-[pressed=true]:bg-neutral-ui-active"
                >
                    <div className="flex w-full min-w-0 flex-1 items-center gap-2">
                        <SiteContentIcon
                            site={site}
                            className="size-4 shrink-0"
                            outlined={true}
                            withBackground={true}
                            size={16}
                        />
                        <div className="min-w-0 truncate text-ui-standout">{site.title}</div>
                        <Icon.ChevronDown className="ml-auto size-4 shrink-0 text-muted" />
                    </div>

                    {site.urls.published ? (
                        <div className="flex w-full min-w-0 items-center justify-between gap-2 text-muted">
                            <Tooltip label={site.urls.published}>
                                <span className="min-w-0 truncate text-ui-small">
                                    {site.urls.published}
                                </span>
                            </Tooltip>
                        </div>
                    ) : site.published && site.visibility === api.SiteVisibility.ShareLink ? (
                        <SidebarEmptyState className="mr-0 ml-0 w-full min-w-0 truncate">
                            Site published as share links
                        </SidebarEmptyState>
                    ) : !site.published ? (
                        <SidebarEmptyState className="mr-0 ml-0 w-full min-w-0 truncate">
                            This site is not published.
                        </SidebarEmptyState>
                    ) : null}
                </PaletteAtomButton>

                <div className="flex border-neutral-subtle border-t">
                    <AtomLink
                        className={siteSwitcherActionClassName}
                        disabled={isSiteEmpty}
                        onPress={() => {
                            analytics.logEvent({
                                ...trackingContext,
                                component: 'preview_button',
                            });
                        }}
                        href={{
                            screen: {
                                type: 'site',
                                organization: organization.id,
                                site: site.id,
                                tab: { type: 'preview' },
                            },
                        }}
                    >
                        <Icon.SitePreview className="size-3" />
                        <span className="min-w-0 truncate">Preview</span>
                    </AtomLink>
                    {shouldShowActionsDivider ? (
                        <span
                            aria-hidden="true"
                            className="shrink-0 self-stretch border-neutral-subtle border-l"
                        />
                    ) : null}
                    {site.urls.published ? (
                        <AtomLink
                            className={siteSwitcherActionClassName}
                            onPress={() => {
                                analytics.logEvent({
                                    ...trackingContext,
                                    component: 'visit_button',
                                });
                            }}
                            href={site.urls.published}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Icon.ExternalLink className="size-3" />
                            <span className="min-w-0 truncate">Visit</span>
                        </AtomLink>
                    ) : site.published && site.visibility === api.SiteVisibility.ShareLink ? (
                        <SiteSwitcherShareLinksButton
                            organizationId={organization.id}
                            site={site}
                            trackingContext={trackingContext}
                        />
                    ) : !site.published ? (
                        <UpgradeDialogProvider>
                            <SiteSwitcherPublishButton
                                organization={organization}
                                site={site}
                                trackingContext={trackingContext}
                            />
                        </UpgradeDialogProvider>
                    ) : null}
                </div>
            </div>

            {canCreateSite ? (
                <DialogRoot {...createSiteDialogTrigger.rootProps}>
                    <CreateSiteDialog organizationId={organization.id} />
                </DialogRoot>
            ) : null}
        </>
    );
}

const siteSwitcherActionClassName =
    'flex min-w-0 flex-1 items-center justify-center gap-1.5 bg-base px-1.5 py-1 text-secondary text-ui-small transition-colors hover:bg-neutral-ui-hover hover:text-base data-[pressed=true]:bg-neutral-ui-active data-[focus-visible]:focus-ring-inset disabled:cursor-not-allowed disabled:opacity-disabled aria-disabled:cursor-not-allowed aria-disabled:opacity-disabled';

function SiteSwitcherShareLinksButton(props: {
    organizationId: string;
    site: api.Site;
    trackingContext: Omit<analytics.ClickAnalyticsEvent, 'component'>;
}) {
    const { organizationId, site, trackingContext } = props;
    const canAdminSite = site.permissions.admin;

    if (!canAdminSite) {
        return null;
    }

    return (
        <AtomLink
            className={siteSwitcherActionClassName}
            onPress={() => {
                analytics.logEvent({
                    ...trackingContext,
                    component: 'share_links_button',
                });
            }}
            href={{
                overlay: null,
                screen: {
                    type: 'site',
                    organization: organizationId,
                    site: site.id,
                    tab: {
                        type: 'settings',
                        section: 'audience',
                    },
                },
            }}
        >
            <Icon.PublishStatusShareLink className="size-4" />
            <span className="min-w-0 truncate">Share links</span>
        </AtomLink>
    );
}

function SiteSwitcherPublishButton(props: {
    organization: api.Organization;
    site: api.Site;
    trackingContext: Omit<analytics.ClickAnalyticsEvent, 'component'>;
}) {
    const { organization, site, trackingContext } = props;
    const publish = useSitePublish({ organization });
    const [isPublishing, wrapPublishing] = useLoadingState();
    const canAdminSite = site.permissions.admin;

    if (!canAdminSite) {
        return null;
    }

    const notPublishableReason = getSiteNotPublishableReason({ site, canAdminSite });

    return (
        <DialogRoot {...publish.dialogRootProps}>
            <AtomButton
                className={clsx(siteSwitcherActionClassName)}
                disabled={Boolean(notPublishableReason) || isPublishing}
                title={notPublishableReason ?? undefined}
                aria-busy={isPublishing || undefined}
                onPress={() => {
                    analytics.logEvent({
                        ...trackingContext,
                        component: 'publish_button',
                    });
                    void wrapPublishing(publish.publishSite(site));
                }}
            >
                {isPublishing ? (
                    <LoadingIndicator size="xsmall" />
                ) : (
                    <Icon.Publish className="size-3" />
                )}
                <span className="min-w-0 truncate">Publish</span>
            </AtomButton>
            {publish.publishDialogElement}
        </DialogRoot>
    );
}
